<script setup>
import { ref, computed, onMounted } from 'vue';
import { useI18n } from 'vue-i18n';
import { useRoute } from 'vue-router';
import { useMapGetter } from 'dashboard/composables/store';
import { useAlert } from 'dashboard/composables';
import SystemAssistantAPI from 'dashboard/api/systemAssistant';
import Button from 'dashboard/components-next/button/Button.vue';

const props = defineProps({ usageOnly: { type: Boolean, default: false } });
const { t } = useI18n();
const assistant = ref(null);
const platform = ref(null);
const apiKey = ref('');
const modelKey = ref('');
const clearModelKey = ref(false);
const modelProvider = ref('openai_compatible');
const savedModelOrigin = ref('');
const modelOrigin = value => {
  try {
    return new URL(value).origin;
  } catch {
    return '';
  }
};
const modelKeyAvailable = computed(
  () =>
    platform.value?.model_key_configured &&
    modelOrigin(platform.value.model_base_url) === savedModelOrigin.value
);
const selectModelProvider = () => {
  modelKey.value = '';
  clearModelKey.value = false;
  platform.value.model_base_url =
    modelProvider.value === 'deepseek'
      ? 'https://api.deepseek.com'
      : 'https://api.openai.com/v1';
  platform.value.llm_model =
    modelProvider.value === 'deepseek' ? 'deepseek-flash' : '';
};
const monthlyLimit = ref(1000);
const inboxDrafts = ref([]);
const previewInbox = ref(null);
const question = ref('');
const answer = ref('');
const previewError = ref(false);
const testing = ref(false);
const route = useRoute();
const accountId = computed(() => route.params.accountId);
const bots = useMapGetter('agentBots/getBots');
const inboxName = id =>
  assistant.value?.inboxes.find(inbox => inbox.id === id)?.name ||
  t('SYSTEM_ASSISTANT.DELETED_INBOX');
const stateText = state =>
  ({
    completed: t('SYSTEM_ASSISTANT.RUN_COMPLETED'),
    queued: t('SYSTEM_ASSISTANT.RUN_QUEUED'),
    running: t('SYSTEM_ASSISTANT.RUN_RUNNING'),
    failed: t('SYSTEM_ASSISTANT.RUN_FAILED'),
    cancelled: t('SYSTEM_ASSISTANT.RUN_CANCELLED'),
  })[state] || state;
const clearApiKey = ref(false);
const busy = ref(false);
const preparing = ref(false);
const loadError = ref(false);
const openingLangflow = ref(false);

const openLangflow = async () => {
  const targetTab = window.open('about:blank', '_blank');
  if (!targetTab) {
    useAlert(t('SYSTEM_ASSISTANT.POPUP_BLOCKED'));
    return;
  }
  targetTab.opener = null;
  openingLangflow.value = true;
  try {
    const { data } = await SystemAssistantAPI.openLangflow();
    if (!targetTab.closed) targetTab.location.replace(data.url);
  } catch {
    targetTab.close();
    useAlert(t('SYSTEM_ASSISTANT.LAUNCH_ERROR'));
  } finally {
    openingLangflow.value = false;
  }
};

const load = async () => {
  loadError.value = false;
  try {
    assistant.value = (await SystemAssistantAPI.get()).data;
    inboxDrafts.value = assistant.value.inboxes.map(inbox => ({ ...inbox }));
    monthlyLimit.value = assistant.value.usage.limit;
    if (!previewInbox.value)
      previewInbox.value = assistant.value.inboxes[0]?.id || null;
    if (!props.usageOnly && assistant.value.can_manage_platform) {
      platform.value = (await SystemAssistantAPI.getPlatform()).data;
      modelProvider.value = platform.value.model_provider;
      savedModelOrigin.value = modelOrigin(platform.value.model_base_url);
    }
  } catch {
    loadError.value = true;
  }
};

const toggle = async event => {
  const enabled = event.target.checked;
  event.target.checked = assistant.value.enabled;
  busy.value = true;
  preparing.value = enabled;
  try {
    assistant.value = (await SystemAssistantAPI.toggle(enabled)).data;
    await load();
    useAlert(t('SYSTEM_ASSISTANT.SAVED'));
  } catch (error) {
    const code = error.response?.data?.error;
    await load();
    let message = t('SYSTEM_ASSISTANT.ERROR');
    if (code === 'model_setup_required') {
      message = assistant.value.can_manage_platform
        ? t('SYSTEM_ASSISTANT.MODEL_SETUP_REQUIRED_ADMIN')
        : t('SYSTEM_ASSISTANT.MODEL_SETUP_REQUIRED');
    } else if (code === 'langflow_unavailable') {
      message = t('SYSTEM_ASSISTANT.LANGFLOW_UNAVAILABLE');
    } else if (code === 'connection_failed') {
      message = t('SYSTEM_ASSISTANT.CONNECTION_FAILED');
    } else if (code === 'configuration_changed') {
      message = t('SYSTEM_ASSISTANT.CONFIGURATION_CHANGED');
    }
    useAlert(message);
  } finally {
    busy.value = false;
    preparing.value = false;
  }
};

const save = async () => {
  busy.value = true;
  try {
    platform.value = (
      await SystemAssistantAPI.savePlatform({
        base_url: platform.value.base_url,
        flow_id: platform.value.flow_id || '',
        api_key: apiKey.value,
        clear_api_key: clearApiKey.value,
        llm_model: platform.value.llm_model || '',
        model_base_url: platform.value.model_base_url,
        model_api_key: modelKey.value,
        clear_model_key: clearModelKey.value,
      })
    ).data;
    apiKey.value = '';
    modelKey.value = '';
    clearModelKey.value = false;
    clearApiKey.value = false;
    await load();
    useAlert(t('SYSTEM_ASSISTANT.SAVED'));
  } catch {
    useAlert(t('SYSTEM_ASSISTANT.ERROR'));
  } finally {
    busy.value = false;
  }
};

const test = async () => {
  busy.value = true;
  try {
    await SystemAssistantAPI.testPlatform();
    await load();
    useAlert(t('SYSTEM_ASSISTANT.TEST_SUCCESS'));
  } catch {
    await load();
    useAlert(t('SYSTEM_ASSISTANT.TEST_ERROR'));
  } finally {
    busy.value = false;
  }
};

const saveInbox = async inbox => {
  busy.value = true;
  try {
    await SystemAssistantAPI.configureInbox({
      inbox_id: inbox.id,
      portal_id: inbox.portal_id,
      bot_id: inbox.bot_id,
    });
    await load();
    useAlert(t('SYSTEM_ASSISTANT.SAVED'));
  } catch {
    useAlert(t('SYSTEM_ASSISTANT.ERROR'));
  } finally {
    busy.value = false;
  }
};
const preview = async () => {
  if (testing.value || !question.value.trim()) return;
  testing.value = true;
  answer.value = '';
  previewError.value = false;
  try {
    answer.value = (
      await SystemAssistantAPI.preview({
        inbox_id: previewInbox.value,
        question: question.value,
      })
    ).data.answer;
  } catch {
    previewError.value = true;
  } finally {
    testing.value = false;
    await load();
  }
};
const saveQuota = async () => {
  busy.value = true;
  try {
    await SystemAssistantAPI.setQuota(monthlyLimit.value);
    await load();
    useAlert(t('SYSTEM_ASSISTANT.SAVED'));
  } catch {
    useAlert(t('SYSTEM_ASSISTANT.ERROR'));
  } finally {
    busy.value = false;
  }
};
onMounted(load);
</script>

<template>
  <section class="border border-n-weak rounded-xl p-6 mb-6 bg-n-solid-1">
    <div v-if="loadError" class="flex items-center gap-3">
      <p class="text-n-slate-11">{{ t('SYSTEM_ASSISTANT.ERROR') }}</p>
      <Button :label="t('SYSTEM_ASSISTANT.RETRY')" @click="load" />
    </div>
    <div v-else-if="assistant">
      <div v-if="!usageOnly" class="flex items-start justify-between gap-6">
        <div>
          <h2 class="text-lg font-medium text-n-slate-12">
            {{ t('SYSTEM_ASSISTANT.TITLE') }}
          </h2>
          <p class="text-sm text-n-slate-11 mt-2">
            {{ t('SYSTEM_ASSISTANT.DESCRIPTION') }}
          </p>
          <p class="text-sm mt-3 text-n-slate-12" role="status">
            {{
              preparing
                ? t('SYSTEM_ASSISTANT.PREPARING')
                : t(`SYSTEM_ASSISTANT.STATUS_${assistant.status.toUpperCase()}`)
            }}
          </p>
        </div>
        <label class="flex items-center gap-2 shrink-0 text-sm text-n-slate-12">
          <input
            type="checkbox"
            role="switch"
            :checked="assistant.enabled"
            :disabled="busy"
            @change="toggle"
          />
          {{ t('SYSTEM_ASSISTANT.ENABLED') }}
        </label>
      </div>
      <div
        v-if="!usageOnly && assistant.can_manage_platform"
        class="mt-4 flex flex-wrap items-center gap-3"
      >
        <Button
          :label="t('SYSTEM_ASSISTANT.OPEN_LANGFLOW')"
          :is-loading="openingLangflow"
          :disabled="openingLangflow"
          icon="i-lucide-external-link"
          @click="openLangflow"
        />
        <span class="text-sm text-n-slate-11">{{
          t('SYSTEM_ASSISTANT.LAUNCH_HINT')
        }}</span>
      </div>
      <p v-if="!usageOnly" class="text-sm text-n-slate-11 mt-3">
        {{ t('SYSTEM_ASSISTANT.HANDOFF_HINT') }}
      </p>
      <div class="mt-5 grid gap-5">
        <div class="rounded-lg bg-n-alpha-1 p-4 text-sm text-n-slate-12">
          <h3 class="font-medium">{{ t('SYSTEM_ASSISTANT.USAGE_TITLE') }}</h3>
          <p class="mt-2">
            {{ t('SYSTEM_ASSISTANT.USAGE_SUMMARY', assistant.usage) }}
          </p>
          <p class="mt-1 text-n-slate-11">
            {{ t('SYSTEM_ASSISTANT.USAGE_HINT') }}
          </p>
        </div>
        <section v-if="!usageOnly" class="border-t border-n-weak pt-5">
          <h3 class="font-medium text-n-slate-12">
            {{ t('SYSTEM_ASSISTANT.INBOX_TITLE') }}
          </h3>
          <p class="text-sm text-n-slate-11 my-2">
            {{ t('SYSTEM_ASSISTANT.INBOX_HINT') }}
          </p>
          <p v-if="!inboxDrafts.length" class="text-sm text-n-slate-11">
            {{ t('SYSTEM_ASSISTANT.NO_INBOX') }}
          </p>
          <div
            v-for="inbox in inboxDrafts"
            :key="inbox.id"
            class="grid gap-3 mt-4 rounded-lg border border-n-weak p-4 md:grid-cols-4 items-end"
          >
            <div class="text-sm text-n-slate-12 font-medium self-center">
              {{ inbox.name }}
            </div>
            <label class="text-sm text-n-slate-12">
              {{ t('SYSTEM_ASSISTANT.RECEPTION_BOT') }}
              <select v-model="inbox.bot_id" :disabled="busy">
                <option :value="null">
                  {{ t('SYSTEM_ASSISTANT.DEFAULT_BOT') }}
                </option>
                <option v-for="bot in bots" :key="bot.id" :value="bot.id">
                  {{ bot.name }}
                </option>
              </select>
            </label>
            <label class="text-sm text-n-slate-12">
              {{ t('SYSTEM_ASSISTANT.KNOWLEDGE') }}
              <select v-model="inbox.portal_id" :disabled="busy">
                <option :value="null">
                  {{ t('SYSTEM_ASSISTANT.NO_KNOWLEDGE') }}
                </option>
                <option
                  v-for="portal in assistant.portals"
                  :key="portal.id"
                  :value="portal.id"
                >
                  {{ portal.name }}
                </option>
              </select>
            </label>
            <Button
              :label="t('SYSTEM_ASSISTANT.SAVE_INBOX')"
              :disabled="busy"
              @click="saveInbox(inbox)"
            />
            <p
              v-if="inbox.external_active && !inbox.bot_id"
              class="text-sm text-n-amber-11 md:col-span-4"
            >
              {{ t('SYSTEM_ASSISTANT.OTHER_INTEGRATION') }}
            </p>
          </div>
          <router-link
            :to="`/app/accounts/${accountId}/portals`"
            class="text-sm text-n-blue-11 inline-block mt-3"
          >
            {{ t('SYSTEM_ASSISTANT.MANAGE_KNOWLEDGE') }}
          </router-link>
        </section>
        <section v-if="!usageOnly" class="border-t border-n-weak pt-5">
          <h3 class="font-medium text-n-slate-12">
            {{ t('SYSTEM_ASSISTANT.PREVIEW_TITLE') }}
          </h3>
          <p class="text-sm text-n-slate-11 mt-2">
            {{ t('SYSTEM_ASSISTANT.PREVIEW_HINT') }}
          </p>
          <form class="grid gap-3 mt-3" @submit.prevent="preview">
            <label class="text-sm text-n-slate-12">
              {{ t('SYSTEM_ASSISTANT.TEST_INBOX') }}
              <select v-model="previewInbox" :disabled="testing || busy">
                <option
                  v-for="inbox in assistant.inboxes"
                  :key="inbox.id"
                  :value="inbox.id"
                >
                  {{ inbox.name }}
                </option>
              </select>
            </label>
            <label class="text-sm text-n-slate-12">
              {{ t('SYSTEM_ASSISTANT.TEST_QUESTION') }}
              <textarea
                v-model="question"
                rows="3"
                maxlength="8000"
                :disabled="testing"
                required
              />
            </label>
            <Button
              type="submit"
              :label="t('SYSTEM_ASSISTANT.SEND_TEST')"
              :is-loading="testing"
              :disabled="busy || testing || !previewInbox || !question.trim()"
            />
          </form>
          <p
            v-if="previewError"
            role="alert"
            class="text-sm text-n-ruby-11 mt-3"
          >
            {{ t('SYSTEM_ASSISTANT.PREVIEW_ERROR') }}
          </p>
          <p
            v-if="answer"
            role="status"
            class="text-sm text-n-slate-12 whitespace-pre-wrap bg-n-alpha-1 rounded-lg p-4 mt-3"
          >
            {{ answer }}
          </p>
        </section>
        <section
          v-if="assistant.recent_runs.length"
          class="border-t border-n-weak pt-5"
        >
          <h3 class="font-medium text-n-slate-12">
            {{ t('SYSTEM_ASSISTANT.HISTORY') }}
          </h3>
          <div class="overflow-x-auto mt-3">
            <table class="w-full text-sm text-start text-n-slate-11">
              <thead>
                <tr>
                  <th class="text-start p-2">
                    {{ t('SYSTEM_ASSISTANT.TIME') }}
                  </th>
                  <th class="text-start p-2">
                    {{ t('SYSTEM_ASSISTANT.TEST_INBOX') }}
                  </th>
                  <th class="text-start p-2">
                    {{ t('SYSTEM_ASSISTANT.SOURCE') }}
                  </th>
                  <th class="text-start p-2">
                    {{ t('SYSTEM_ASSISTANT.RESULT') }}
                  </th>
                </tr>
              </thead>
              <tbody>
                <tr
                  v-for="run in assistant.recent_runs"
                  :key="run.id"
                  class="border-t border-n-weak"
                >
                  <td class="p-2">
                    {{ new Date(run.created_at).toLocaleString() }}
                  </td>
                  <td class="p-2">{{ inboxName(run.inbox_id) }}</td>
                  <td class="p-2">
                    {{
                      run.source === 'preview'
                        ? t('SYSTEM_ASSISTANT.SOURCE_TEST')
                        : t('SYSTEM_ASSISTANT.SOURCE_CHAT')
                    }}
                  </td>
                  <td class="p-2">{{ stateText(run.status) }}</td>
                </tr>
              </tbody>
            </table>
          </div>
        </section>
      </div>
      <details
        v-if="!usageOnly && platform"
        class="mt-5 border-t border-n-weak pt-4"
      >
        <summary class="cursor-pointer text-sm font-medium text-n-slate-12">
          {{ t('SYSTEM_ASSISTANT.PLATFORM') }}
        </summary>
        <p class="text-sm text-n-slate-11 my-3">
          {{ t('SYSTEM_ASSISTANT.PLATFORM_HINT') }}
        </p>
        <form class="grid gap-3 max-w-xl" @submit.prevent="save">
          <template v-if="platform.managed || !platform.flow_id">
            <label class="text-sm text-n-slate-12">
              {{ t('SYSTEM_ASSISTANT.MODEL_PROVIDER') }}
              <select
                v-model="modelProvider"
                :disabled="busy"
                @change="selectModelProvider"
              >
                <option value="openai_compatible">
                  {{ t('SYSTEM_ASSISTANT.COMPATIBLE_PROVIDER') }}
                </option>
                <option value="deepseek">
                  {{ t('SYSTEM_ASSISTANT.DEEPSEEK_PROVIDER') }}
                </option>
              </select>
            </label>
            <p
              v-if="modelProvider === 'deepseek'"
              class="text-sm text-n-slate-11"
            >
              {{ t('SYSTEM_ASSISTANT.DEEPSEEK_HINT') }}
              <a
                href="https://platform.deepseek.com/api_keys"
                target="_blank"
                rel="noopener noreferrer"
                class="text-n-brand"
              >
                {{ t('SYSTEM_ASSISTANT.DEEPSEEK_KEY_LINK') }}
              </a>
            </p>
            <p
              v-if="platform.model_key_configured && !modelKeyAvailable"
              class="text-sm text-n-slate-11"
            >
              {{ t('SYSTEM_ASSISTANT.MODEL_KEY_SWITCH_HINT') }}
            </p>
            <label class="text-sm text-n-slate-12"
              >{{ t('SYSTEM_ASSISTANT.MODEL_URL')
              }}<input
                v-model="platform.model_base_url"
                type="url"
                required
                :readonly="modelProvider === 'deepseek'"
                :disabled="busy"
            /></label>
            <label class="text-sm text-n-slate-12"
              >{{ t('SYSTEM_ASSISTANT.MODEL_NAME')
              }}<input
                v-model="platform.llm_model"
                :list="
                  modelProvider === 'deepseek' ? 'deepseek-models' : undefined
                "
                type="text"
                maxlength="200"
                :disabled="busy"
            /></label>
            <datalist v-if="modelProvider === 'deepseek'" id="deepseek-models">
              <option value="deepseek-flash" />
              <option value="deepseek-v4-pro" />
            </datalist>
            <label class="text-sm text-n-slate-12"
              >{{ t('SYSTEM_ASSISTANT.MODEL_KEY')
              }}<input
                v-model="modelKey"
                type="password"
                autocomplete="new-password"
                :placeholder="t('SYSTEM_ASSISTANT.KEY_HINT')"
                :disabled="busy"
            /></label>
            <p class="text-sm text-n-slate-11">
              {{
                modelKeyAvailable
                  ? t('SYSTEM_ASSISTANT.MODEL_KEY_SET')
                  : t('SYSTEM_ASSISTANT.MODEL_KEY_EMPTY')
              }}
            </p>
            <label
              v-if="modelKeyAvailable"
              class="flex gap-2 text-sm text-n-slate-12"
              ><input
                v-model="clearModelKey"
                type="checkbox"
                :disabled="busy"
              />{{ t('SYSTEM_ASSISTANT.CLEAR_MODEL_KEY') }}</label
            >
          </template>
          <details class="mt-2">
            <summary class="text-sm text-n-slate-11 cursor-pointer">
              {{ t('SYSTEM_ASSISTANT.ADVANCED') }}
            </summary>
            <label class="text-sm text-n-slate-12">
              {{ t('SYSTEM_ASSISTANT.URL') }}
              <input
                v-model="platform.base_url"
                type="url"
                required
                :disabled="busy"
              />
            </label>
            <label class="text-sm text-n-slate-12">
              {{ t('SYSTEM_ASSISTANT.FLOW') }}
              <input v-model="platform.flow_id" type="text" :disabled="busy" />
            </label>
            <label class="text-sm text-n-slate-12">
              {{ t('SYSTEM_ASSISTANT.KEY') }}
              <input
                v-model="apiKey"
                type="password"
                autocomplete="new-password"
                :placeholder="t('SYSTEM_ASSISTANT.KEY_HINT')"
                :disabled="busy"
              />
            </label>
            <span class="text-sm text-n-slate-11">
              {{
                t(
                  platform.api_key_configured
                    ? 'SYSTEM_ASSISTANT.KEY_SET'
                    : 'SYSTEM_ASSISTANT.KEY_EMPTY'
                )
              }}
            </span>
            <label
              v-if="platform.api_key_configured"
              class="flex items-center gap-2 text-sm text-n-slate-12"
            >
              <input v-model="clearApiKey" type="checkbox" :disabled="busy" />
              {{ t('SYSTEM_ASSISTANT.CLEAR_KEY') }}
            </label>
          </details>
          <div class="flex gap-3 mt-1">
            <Button
              type="submit"
              :label="t('SYSTEM_ASSISTANT.SAVE')"
              :disabled="busy"
            />
            <Button
              type="button"
              :label="t('SYSTEM_ASSISTANT.TEST')"
              :disabled="busy"
              @click="test"
            />
          </div>
        </form>
        <form class="mt-5 flex items-end gap-3" @submit.prevent="saveQuota">
          <label class="text-sm text-n-slate-12"
            >{{ t('SYSTEM_ASSISTANT.QUOTA_LABEL')
            }}<input
              v-model.number="monthlyLimit"
              type="number"
              min="0"
              max="1000000"
              step="1"
              required
              :disabled="busy"
          /></label>
          <Button
            type="submit"
            :label="t('SYSTEM_ASSISTANT.SAVE_QUOTA')"
            :disabled="busy"
          />
        </form>
      </details>
    </div>
    <p v-else class="text-sm text-n-slate-11">
      {{ t('SYSTEM_ASSISTANT.LOADING') }}
    </p>
  </section>
</template>
